import classNames from 'classnames'
import React, { FC } from 'react'

import { Body1, Body2, Caption1, Caption2, Overline } from '@ui/Typography'
import { SurfaceColorType } from '@ui/design-system/colors/colors'
import { IconRenderer, IconRendererType } from '@ui/icons/utils/IconRenderer'

import { Box } from '@shared/components/Box'

import styles from './Pimple.module.scss'

type PimpleSize = 'x6' | 'x16' | 'x20' | 'x24' | 'x32'

interface PimpleBase {
  bg?: SurfaceColorType
}

interface PimpleX6 extends PimpleBase {
  size: Extract<PimpleSize, 'x6'>
  label?: never
  icon?: never
}

interface PimpleDefault extends PimpleBase {
  size?: Exclude<PimpleSize, 'x6'>
  label?: string
  icon?: IconRendererType
}

export type PimpleProps = PimpleX6 | PimpleDefault

const LABELLESS_SIZES = new Set<PimpleSize>(['x6'])

export const Pimple: FC<PimpleProps> = (props) => {
  const { label, size = 'x20', bg = 'surface-secondary-med-em', icon } = props

  const pimpleClasses = classNames(styles.pimple, styles[`size-${size}`])
  const iconSize = getIconSize(size)
  const LabelElement = getLabelSize(size)
  const isLabelVisible = !LABELLESS_SIZES.has(size)

  return (
    <Box flex bg={bg} className={pimpleClasses}>
      <LabelElement
        color='text-black'
        bold
        className={classNames(styles.label, { [styles.labelWithText]: !!label })}
      >
        {isLabelVisible ? <span>{label}</span> : null}
        {icon && <IconRenderer icon={icon} size={iconSize} />}
      </LabelElement>
    </Box>
  )
}

const getLabelSize = (size: PimpleSize) => {
  return {
    x6: Overline,
    x16: Caption2,
    x20: Caption1,
    x24: Body2,
    x32: Body1,
  }[size]
}

const getIconSize = (size: PimpleSize): number => {
  return {
    x6: 0,
    x16: 12,
    x20: 12,
    x24: 16,
    x32: 18,
  }[size]
}
